import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { quotesAPI } from '../services/api';
import { getChecklistForService } from '../data/serviceChecklists';

const QuoteModal = ({ open, onClose, provider }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    description: '',
    preferredDate: ''
  });
  const [selectedChecklist, setSelectedChecklist] = useState([]);

  const checklist = provider?.category ? getChecklistForService(provider.category) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Combine checklist items with description
      const checklistText = selectedChecklist.length > 0 
        ? '\n\nWork Required:\n- ' + selectedChecklist.join('\n- ')
        : '';
      
      await quotesAPI.create({
        ...formData,
        providerId: provider?.id,
        description: formData.description + checklistText
      });
      toast({
        title: 'Quote Request Sent!',
        description: `${provider?.name || 'The provider'} will contact you within ${provider?.responseTime.toLowerCase() || '24 hours'}.`,
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        description: '',
        preferredDate: ''
      });
      setSelectedChecklist([]);
      onClose();
    } catch (error) {
      toast({
        title: 'Request Failed',
        description: 'Could not submit quote request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleChecklistItem = (item) => {
    setSelectedChecklist(prev => 
      prev.includes(item) 
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Request a Quote from {provider?.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="service">Service Needed *</Label>
            <Input
              id="service"
              name="service"
              type="text"
              placeholder="e.g., Kitchen plumbing repair"
              value={formData.service}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          {/* Service-Specific Checklist */}
          {checklist && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <Label className="text-base font-semibold mb-3 block">{checklist.title}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {checklist.options.map((option, index) => (
                  <label
                    key={index}
                    className="flex items-start space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedChecklist.includes(option)}
                      onChange={() => toggleChecklistItem(option)}
                      className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="description">Additional Details *</Label>
            <Textarea
              id="description"
              name="description"
              placeholder="Please provide more details about your project, timeline, budget, or any specific requirements..."
              value={formData.description}
              onChange={handleChange}
              required
              className="mt-1 min-h-[100px]"
            />
          </div>

          <div>
            <Label htmlFor="preferredDate">Preferred Date</Label>
            <div className="relative">
              <Input
                id="preferredDate"
                name="preferredDate"
                type="date"
                value={formData.preferredDate}
                onChange={handleChange}
                className="mt-1"
              />
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Response time:</strong> {provider?.responseTime || 'Within 24 hours'}
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" disabled={loading}>
              {loading ? 'Sending...' : 'Send Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;